import { useEffect, useMemo, useState } from "react";
import "react-tooltip/dist/react-tooltip.css";
import React from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchMedications,
  setClinicalServiceName,
  setClinicalServiceSteps,
} from "../../../../redux/modules/clinical-services/actions";
import { useParams } from "react-router-dom";
import { fetchNotepad } from "../../../../redux/modules/patient/actions";
import PrescriptionTabs from "../../clinical-services/service-steps/education/prescription-tabs";
import SubNavigation from "../../clinical-services/common/sub-navigation";
import VaccineTable from "../../clinical-services/vaccines/vaccineTable";
import CustomInput from "../../../common/CustomInput";
import { pickFirstValidServiceDate } from "../../../../helpers/functions";

const VaccineDashboard = () => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const { medications = [] } = useSelector((state) => state.clinicalService || {});
  const [activeTab, setActiveTab] = useState({ name: "list", label: "Vaccine List" });
  const [vaccineCategoryFilter, setVaccineCategoryFilter] = useState("all");
  const [dateFilter, setDateFilter] = useState(null);

  const vaccineTabs = [
    { name: "dashboard", label: "Vaccine Dashboard", comingSoon: true },
    { name: "list", label: "Vaccine List" },
    { name: "history", label: "Vaccine History Logs", comingSoon: true },
  ];

  useEffect(() => {
    dispatch(fetchNotepad(id));
    dispatch(fetchMedications(id));
    dispatch(setClinicalServiceName(null));
    dispatch(setClinicalServiceSteps(null));
  }, [dispatch, id]);

  const vaccineCategoryOptions = [
    { value: "all", label: "All Categories" },
    { value: "Adults & Vaccines", label: "Adults & Vaccines" },
    { value: "Travel & Vaccines", label: "Travel & Vaccines" },
    { value: "Pediatrics & Vaccines", label: "Pediatrics & Vaccines" },
    { value: "Pregnancy & Vaccines", label: "Pregnancy & Vaccines" },
    { value: "Conditions & Vaccines", label: "Conditions & Vaccines" },
  ];

  const allVaccines = useMemo(
    () =>
      (medications || [])
        .filter((med) => String(med?.medicationType || "").toLowerCase() === "vaccine")
        .map((med) => ({
          ...med,
          vaccineCategory: med?.vaccineCategory || "Adults & Vaccines",
        })),
    [medications]
  );

  const matchesDateFilter = (vaccine) => {
    if (!dateFilter) return true;
    const srcDate = pickFirstValidServiceDate(
      vaccine?.doseDate,
      vaccine?.startDate,
      vaccine?.created,
      vaccine?.createdAt
    );
    if (!srcDate) return false;
    const vaccineDate = new Date(srcDate);
    const selected = new Date(dateFilter);
    if (Number.isNaN(vaccineDate.getTime()) || Number.isNaN(selected.getTime())) {
      return true;
    }
    return vaccineDate.toDateString() === selected.toDateString();
  };

  const filteredVaccines = useMemo(
    () =>
      allVaccines.filter((vaccine) => {
        const matchesCategory =
          vaccineCategoryFilter === "all" || vaccine.vaccineCategory === vaccineCategoryFilter;
        return matchesCategory && matchesDateFilter(vaccine);
      }),
    [allVaccines, vaccineCategoryFilter, dateFilter]
  );

  const renderTabContent = () => {
    switch (activeTab?.name) {
      case "dashboard":
        return <div className="w-full h-full" />;
      case "list":
        return (
          <div className="flex flex-col w-full h-full pb-4 overflow-visible gap-3">
            {/* Header row: title left, filters right */}
            <div className="flex items-center justify-between gap-6">
              <h4 className="text-xl font-semibold text-teal-300 px-[5px]">Vaccines</h4>
              <div className="flex gap-6">
                <div className="w-[280px]">
                  <CustomInput
                    options={vaccineCategoryOptions}
                    name="vaccineCategory"
                    className="w-full text-gray-800 font-semibold"
                    label=""
                    type="dropdown"
                    menuPlacement="bottom"
                    value={vaccineCategoryFilter}
                    onChange={(e) => setVaccineCategoryFilter(e.value)}
                    placeholder="Vaccine Category"
                  />
                </div>
                <div className="w-[280px]">
                  <CustomInput
                    name="serviceDate"
                    className="w-full text-gray-800 font-semibold"
                    label=""
                    type="date"
                    value={dateFilter}
                    onChange={setDateFilter}
                    placeholder="Select Date"
                  />
                </div>
              </div>
            </div>

            {/* Tables */}
            <div className="relativespace-y-6">
              <VaccineTable
                headerName="Vaccines"
                records={filteredVaccines}
                showHeader={false}
              />
            </div>

          </div>
        );
      case "history":
        return (
          <div className="w-full text-2xl font-bold text-gray-800 gap-5 h-full flex items-center justify-center">
            <div className="text-center">
              <h2 className="text-3xl font-bold text-teal-600 mb-4">
                Vaccine History
              </h2>
              <p className="text-lg text-gray-600">Coming Soon</p>
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="flex flex-col flex-1 min-h-0 w-full">
      <div className="flex flex-col gap-[10px]">
        {/* Top header with actions */}
        <div className="flex border-b-[7px] border-gray-20 items-center justify-between px-2 py-2">
          <h2 className="text-2xl font-bold text-teal-300">Vaccine Health</h2>
          <SubNavigation />
        </div>
        {/* Tabs */}
        <PrescriptionTabs
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          tabs={vaccineTabs}
        />


        {/* Tab Content */}
        <div className="flex-1 min-h-0 bg-white overflow-visible">
          {renderTabContent()}
        </div>
      </div>
    </div>
  );
};

export default VaccineDashboard;
